/**
 * Quick Add Lead modal — manual walk-in / phone-call lead entry.
 *
 * Triggered from a "+ Add Lead" button in the Dealer Portal Leads tab.
 * On success the parent calls onCreated(lead) so the table refreshes.
 */
import { useState } from "react";
import { X, UserPlus, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const SOURCES = [
  "Walk-In", "Phone Call", "Service Drive", "Trade Walk-Up",
  "Referral", "Repeat Customer", "Social Media DM", "Other",
];

export default function QuickAddLeadModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "",
    lead_source: "Phone Call",
    vehicle_title: "", message: "", preferred_contact: "Either",
  });
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags((t) => [...t, v]);
    setTagInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/dealer/leads/quick-add", { ...form, tags });
      onCreated?.(data.lead);
      onClose();
      setForm({ name: "", phone: "", email: "", address: "", lead_source: "Phone Call", vehicle_title: "", message: "", preferred_contact: "Either" });
      setTags([]);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't save lead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3" role="dialog" aria-modal="true" data-testid="quick-add-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] z-[101]">
        <header className="p-4 border-b border-[#D9E1EA] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-[var(--ssc-blue)]" />
            <h2 className="text-lg font-black text-[var(--ssc-navy)]">Quick Add Lead</h2>
          </div>
          <button onClick={onClose} className="text-[#6b7c93] p-1" data-testid="quick-add-close"><X size={20} /></button>
        </header>
        <form onSubmit={submit} className="p-4 overflow-y-auto space-y-3" data-testid="quick-add-form">
          <Field label="Name *">
            <input className="ssc-input" value={form.name} onChange={set("name")} placeholder="Full name" required data-testid="qa-name" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Phone">
              <input className="ssc-input" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="(614) 555-1212" data-testid="qa-phone" />
            </Field>
            <Field label="Email">
              <input className="ssc-input" type="email" value={form.email} onChange={set("email")} placeholder="name@example.com" data-testid="qa-email" />
            </Field>
          </div>
          <Field label="Address">
            <input className="ssc-input" value={form.address} onChange={set("address")} placeholder="Street, City, State" data-testid="qa-address" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Source">
              <select className="ssc-select" value={form.lead_source} onChange={set("lead_source")} data-testid="qa-source">
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Preferred Contact">
              <select className="ssc-select" value={form.preferred_contact} onChange={set("preferred_contact")} data-testid="qa-pref">
                <option value="Either">Either</option>
                <option value="Phone">Phone</option>
                <option value="Text">Text</option>
                <option value="Email">Email</option>
              </select>
            </Field>
          </div>
          <Field label="Vehicle Interest">
            <input className="ssc-input" value={form.vehicle_title} onChange={set("vehicle_title")} placeholder="e.g. 2024 Silverado 1500 LT" data-testid="qa-vehicle" />
          </Field>
          <Field label="Notes / Initial Message">
            <textarea className="ssc-input min-h-[80px]" value={form.message} onChange={set("message")} placeholder="What did they want? Anything to remember?" data-testid="qa-message" />
          </Field>
          <Field label="Tags">
            <div className="flex gap-2 flex-wrap">
              {tags.map((t) => (
                <span key={t} className="text-xs font-bold px-2 py-1 rounded-full bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] border border-[var(--ssc-blue)]/30">
                  {t} <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="ml-1 hover:text-red-600">×</button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag…"
                className="ssc-input h-9 text-sm w-32 flex-1"
                data-testid="qa-tag-input"
              />
            </div>
          </Field>

          {err && <div className="text-sm text-red-600" data-testid="qa-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary flex-1" data-testid="qa-submit">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Add Lead
            </button>
            <button type="button" onClick={onClose} className="ssc-btn ssc-btn-secondary" data-testid="qa-cancel">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);
